import { Logger } from "./logger";
import {
  DQ_ID_STORAGE_KEY,
  DqTagEnvHost,
  DqTagEventApi,
  MAX_EVENT_FINGERPRINTS,
  EVENT_SAMPLING_RATE,
  DEBUG_QUERY_PARAM,
} from "./config";
import {
  type DqTagConfig,
  type BaseEvent,
  type WitnessEventData,
  type ProfileEventData,
  type IdentifyEventData,
  type IDqTag,
  type DqTagCommandExecutor,
  type IdentityClientConfig,
} from "./models";
import { ensurePresent, generateRandomString, isValidUrl } from "./utils";
import { ConsentProcessor } from "./consentProcessor";
import { DqIdentityClient } from "./dqIdentityClient";
import { debugBus } from "./debug/bus";
import { maybeActivateDebugSdk } from "./debug/activate";

export class DqTag implements IDqTag {
  private _clientId: string;
  private _apiUrl: string;
  private _logger: Logger;
  private _consentProcessor: ConsentProcessor;
  private _identityClient: DqIdentityClient;
  private _isIdentityClientInitialized: boolean = false;
  private _initialized = false;
  private _sentEventFingerprints: Set<string>;
  private _onGetId?: DqTagConfig["onGetId"];
  private _autoInitIdentity: boolean = false;
  private readonly _clientSourceValue: string = "dqtag-sdk";
  private _clientSource: string;

  constructor() {
    this._clientId = "";
    this._apiUrl = DqTagEnvHost.prod;
    this._logger = new Logger("DqTag");
    this._consentProcessor = new ConsentProcessor(this._logger);
    this._identityClient = new DqIdentityClient();
    this._clientSource = this._clientSourceValue;

    this._sentEventFingerprints = new Set();
  }

  get currentOrigin(): string {
    return globalThis.location.origin;
  }

  get currentHref(): string {
    return window.location.href.split("?")[0].split("#")[0];
  }

  /**
   * @deprecated Use getId() from DqIdentityClient instead. This legacy ID will be removed in a future version.
   */
  private get dqId(): string | null {
    return localStorage.getItem(DQ_ID_STORAGE_KEY) ?? null;
  }

  /**
   * @deprecated Use getId() from DqIdentityClient instead. This legacy ID will be removed in a future version.
   */
  private set dqId(value: string | null) {
    if (value == null) {
      localStorage.removeItem(DQ_ID_STORAGE_KEY);
      return;
    }
    localStorage.setItem(DQ_ID_STORAGE_KEY, value);
  }

  private initializeIdentityClientIfConsented(config: DqTagConfig): void {
    const identityConfig: IdentityClientConfig = {
      firstPartyURL: config.firstPartyURL,
      handshakeURL: config.handshakeURL,
      enableThirdParty: config.enableThirdParty,
      syncAfter: config.syncAfter,
      fpVersion: config.fpVersion,
      clientSource: config.clientSource || this._clientSourceValue,
    };
    this._identityClient.initialize(identityConfig);
    this._isIdentityClientInitialized = true;
  }

  public async initialize(config: DqTagConfig): Promise<void> {
    this._clientSource = config.clientSource || this._clientSourceValue;

    this.applyConfiguration(config);

    await this._consentProcessor.initialize(config.tcfVendorId);

    if (config.autoInitIdentity) {
      this._autoInitIdentity = true;
      this.initializeIdentityClientIfConsented(config);
    }
    this._initialized = true;

    debugBus.push({ kind: "sdkInit", sdk: "dqtag", ts: Date.now() });
  }

  public async getId(): Promise<string> {
    if (this._identityClient == null) {
      throw new Error("Identity client is not initialized");
    }

    return new Promise<string>((resolve, reject) => {
      this._consentProcessor
        .runIfConsented(async () => {
          try {
            const id =
              (await this._onGetId?.(() => this._identityClient.getId())) ??
              (await this._identityClient.getId());
            resolve(id);
          } catch (error) {
            reject(error instanceof Error ? error : new Error(String(error)));
          }
        })
        .catch(reject);
    });
  }

  public async logPageViewWitness(): Promise<Response | undefined> {
    await this.logIdentifyWithOneIdIfExists();

    return this.logWitness({
      type: "page_view",
      properties: {
        url: this.currentHref,
      },
    });
  }

  private _hasIdentityClient(): boolean {
    return this._isIdentityClientInitialized || this._autoInitIdentity;
  }

  private async logIdentifyWithOneIdIfExists(): Promise<void> {
    if (!this._hasIdentityClient()) {
      this._logger.printDebug(
        "No Identity client, use getId from OneIdSdk if available",
      );
      if (typeof window.OneIdSDK?.firstParty?.getId === "function") {
        window.OneIdSDK.firstParty.getId(
          (id) => {
            this._logger.printDebug("Retrieved OneId:", id);
            void this.logIdentify({ oneId: id });
          },
          (error) => {
            this._logger.printDebug("Failed to retrieve OneId:", error);
          },
        );
      } else {
        this._logger.printDebug("OneIdSDK not available");
      }
      return;
    }

    try {
      await this.logIdentify({ oneId: await this.getId() });
    } catch (error) {
      this._logger.printDebug("Failed to retrieve OneId:", error);
    }
  }

  public async logPageView(
    userId: string,
    idName: string,
  ): Promise<Awaited<Response | undefined>[] | undefined> {
    if (!userId) {
      this._logger.printError("userId is required");
      return;
    }
    if (!idName) {
      this._logger.printError("idName is required");
      return;
    }
    return Promise.all([
      this.logWitness({
        type: "page_view",
        properties: {
          url: this.currentHref,
        },
      }),
      this.logIdentify({
        [idName]: userId,
      }),
    ]);
  }

  public async logWitness(
    eventData: WitnessEventData,
  ): Promise<Response | undefined> {
    if (typeof eventData !== "object" || !eventData) {
      this._logger.printError("Invalid data for witness event");
    }
    return this.submitEvent(DqTagEventApi.witness, eventData);
  }

  public async logIdentify(
    eventData: IdentifyEventData,
  ): Promise<Response | undefined> {
    if (typeof eventData !== "object" || !eventData) {
      this._logger.printError("Invalid data for identify event");
    }
    return this.submitEvent(DqTagEventApi.identify, eventData);
  }

  public async logProfile(
    eventData: ProfileEventData,
  ): Promise<Response | undefined> {
    if (typeof eventData !== "object" || !eventData) {
      this._logger.printError("Invalid data for profile event");
    }
    return this.submitEvent(DqTagEventApi.profile, eventData);
  }

  public flushCommandQueue(): void {
    window.dqtag = window.dqtag ?? {};
    window.dqtag.cmd = window.dqtag.cmd ?? [];
    const logger = this._logger;
    // TODO: replace method patching with command processor that exposes method push
    window.dqtag.cmd.push = function (...args: DqTagCommandExecutor[]): number {
      if (args.length === 0) {
        return 0;
      }
      if (!args.every((fn) => typeof fn === "function")) {
        logger.printError("DqTag: All commands must be functions");
        return 0;
      }
      args.forEach((fn) => {
        try {
          if (!window.dqtag.instance) {
            throw new Error(
              "Instance is not initialized, unable to execute command",
            );
          }
          fn(window.dqtag.instance);
        } catch (error) {
          logger.printError("DQTag: ", error);
        }
      });
      return 0;
    };
    while (window.dqtag.cmd.length > 0) {
      const fn = window.dqtag.cmd.shift();
      if (!fn) continue;
      window.dqtag.cmd.push(fn);
    }
  }

  private applyConfiguration(config: DqTagConfig): void {
    try {
      if (!config || typeof config !== "object") {
        throw new Error(
          "`config` must be an object holding a `clientId` property",
        );
      }
      if (config.fpVersion && config.fpVersion !== "v4") {
        throw new Error("`fpVersion` must be 'v4' if specified");
      }
      const {
        clientId,
        debug = false,
        disableTcf = false,
        host = DqTagEnvHost.prod,
      } = config;
      if (!clientId || clientId.trim() === "") {
        throw new Error("`clientId` is required and cannot be empty");
      }
      this._clientId = config.clientId;
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this._onGetId = config.onGetId;
      if (host) {
        if (typeof host !== "string") {
          throw new Error("`host` must be a string");
        }
        if (!isValidUrl(host)) {
          throw new Error("`host` must be a valid URL");
        }
        this._apiUrl = host;
      }
      if (disableTcf !== undefined && typeof disableTcf === "boolean") {
        this._consentProcessor.disabled = disableTcf;
        if (disableTcf) {
          this._logger.printDebug("TCF is disabled");
        }
      }
      // Enable debug mode from config or query parameter
      const urlParams = new URLSearchParams(window.location.search);
      const debugFromUrl = urlParams.get(DEBUG_QUERY_PARAM) === "true";

      if (
        debugFromUrl ||
        (debug !== undefined && typeof debug === "boolean" && debug)
      ) {
        this._logger.debug = true;
        this._logger.printDebug(
          "Debug mode is enabled",
          debugFromUrl ? "(via query parameter)" : "(via config)",
        );
      }
      maybeActivateDebugSdk({
        debugSdk: config.debugSdk,
        debugSdkUrl: config.debugSdkUrl,
      });
    } catch (error) {
      this._logger.printError(error instanceof Error ? error.message : error);
    }
  }

  private createEventFingerprint(event: BaseEvent): string {
    // Create fingerprint from all event properties except timestamp
    const fingerprintData = {
      api: event.api,
      dqId: event.dqId,
      clientId: event.clientId,
      site: event.site,
      consent: event.consent,
      event: event.event,
    };
    // Use deterministic JSON stringification with sorted keys
    return JSON.stringify(fingerprintData, (_, value: unknown) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return Object.keys(value)
          .sort()
          .reduce(
            (sorted, key) => {
              sorted[key] = (value as Record<string, unknown>)[key];
              return sorted;
            },
            {} as Record<string, unknown>,
          );
      }
      return value;
    });
  }

  private async submitEvent(
    eventApi: DqTagEventApi,
    eventData: BaseEvent["event"],
  ): Promise<Response | undefined> {
    try {
      if (!eventData || typeof eventData !== "object") {
        throw new Error("Invalid event data provided");
      }
      if (!this._clientId || !eventApi) {
        throw new Error("Client ID or dqId or eventApi is not set");
      }
      await ensurePresent(
        () =>
          (this._initialized &&
            (this._consentProcessor.disabled ||
              this._consentProcessor.loaded)) ||
          null,
        // We might want to set reasonable timeout here
        Infinity,
      );
      this.dqId = this._consentProcessor.hasConsentOrNotApplied
        ? (this.dqId ?? generateRandomString())
        : null;
      const event: BaseEvent = {
        api: this._consentProcessor.hasConsentOrNotApplied
          ? eventApi
          : DqTagEventApi.log,
        timestamp: Date.now(),
        dqId: this._consentProcessor.hasConsentOrNotApplied
          ? (this.dqId ?? "")
          : "",
        clientId: this._clientId,
        site: this.currentOrigin,
        consent: this._consentProcessor.payload,
        event: this._consentProcessor.hasConsentOrNotApplied
          ? eventData
          : { msg: "invalid consent" },
      };
      let fingerprint: string | undefined;
      // Check for duplicate events except for witness events
      if (event.api !== DqTagEventApi.witness) {
        fingerprint = this.createEventFingerprint(event);
        if (this._sentEventFingerprints.has(fingerprint)) {
          this._logger.printDebug(
            "Duplicate event detected, skipping submission",
          );
          return;
        }
      }
      // Subsample log events
      if (
        event.api === DqTagEventApi.log &&
        Math.random() >= EVENT_SAMPLING_RATE
      ) {
        this._logger.printDebug(
          `Log event skipped due to sampling (${EVENT_SAMPLING_RATE * 100}% rate)`,
        );
        return;
      }
      // Add fingerprint to set with FIFO eviction
      if (this._sentEventFingerprints.size >= MAX_EVENT_FINGERPRINTS) {
        // Remove the first (oldest) entry
        const firstKey = this._sentEventFingerprints.values().next().value;
        if (firstKey !== undefined) {
          this._sentEventFingerprints.delete(firstKey);
        }
      }
      if (fingerprint) {
        this._sentEventFingerprints.add(fingerprint);
      }
      const requestInit: RequestInit = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Client-Source": this._clientSource,
        },
        body: JSON.stringify(event),
      };
      this._logger.printDebug(
        `Sending event to ${this._apiUrl}:`,
        event,
        requestInit,
      );
      debugBus.push({
        kind: "fetch",
        phase: "start",
        method: "POST",
        url: this._apiUrl,
        body: event,
        ts: Date.now(),
      });
      let response: Response;
      try {
        response = await fetch(this._apiUrl, requestInit);
      } catch (fetchErr) {
        debugBus.push({
          kind: "fetch",
          phase: "end",
          method: "POST",
          url: this._apiUrl,
          error:
            fetchErr instanceof Error ? fetchErr.message : String(fetchErr),
          ts: Date.now(),
        });
        throw fetchErr;
      }
      debugBus.push({
        kind: "fetch",
        phase: "end",
        method: "POST",
        url: this._apiUrl,
        status: response.status,
        ts: Date.now(),
      });
      this._logger.printDebug(
        `DQTag: Received response from ${this._apiUrl}:`,
        {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
        },
      );
      if (!response.ok) {
        this._logger.printError(
          `DQTag.logEvent: HTTP error. Status: ${response.status}`,
        );
      }
      return response;
    } catch (error) {
      this._logger.printError(error);
      return undefined;
    }
  }
}
